/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Administrator Profile & Security Access Console
 * PATH: src/routes/Profile.js
 * COMPLIANCE: ISO 27001, DPDP Act 2023, RBI IT Framework Guidelines
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Spin,
  Input,
  Form,
  Row,
  Col,
  Card,
  Modal,
  Button,
  message,
  Table,
  Tag,
  Typography,
  Tooltip,
  Descriptions,
  Avatar,
  Space,
  Divider,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  CopyOutlined,
  GlobalOutlined,
  LaptopOutlined,
  LockOutlined,
  KeyOutlined,
  ThunderboltFilled,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import "./Profile.css";

const { Title, Text } = Typography;

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loginHistoryData, setLoginHistoryData] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);

  const [form] = Form.useForm();
  const token = localStorage.getItem("token");

  // ================================
  // FETCH ADMIN PROFILE
  // ================================
  const fetchProfile = useCallback(async () => {
    try {
      setIsProfileLoading(true);
      const res = await axios.get(
        "https://billpay.mtmpay.in/Admin/api/admin/profile",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res?.data?.status === "1") {
        setProfile(res.data.data);
        form.setFieldsValue({
          name: res?.data?.data?.name || "",
          email: res?.data?.data?.email || "",
          old_password: "",
          new_password: "",
          confirm_password: "",
        });
      } else {
        message.error(res?.data?.message || "Unable to fetch administrator profile.");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      message.error(error?.response?.data?.message || "Failed to load admin profile.");
    } finally {
      setIsProfileLoading(false);
    }
  }, [token, form]);

  // ================================
  // FETCH ADMIN LOGIN HISTORY
  // ================================
  const fetchLoginHistory = useCallback(async () => {
    try {
      setLoginHistoryLoading(true);
      const res = await axios.post(
        "https://billpay.mtmpay.in/Admin/api/admin/admin-login-history",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res?.data?.status === 1) {
        setLoginHistoryData(Array.isArray(res?.data?.data) ? res.data.data : []);
      } else {
        setLoginHistoryData([]);
      }
    } catch (error) {
      console.error("Login History Error:", error);
      setLoginHistoryData([]);
    } finally {
      setLoginHistoryLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
    fetchLoginHistory();
  }, [fetchProfile, fetchLoginHistory]);

  // ================================
  // UPDATE PROFILE / CREDENTIALS
  // ================================
  const handleUpdate = async (values) => {
    try {
      setIsUpdating(true);
      const payload = {
        name: values.name,
        email: values.email,
        old_password: values.old_password,
        new_password: values.new_password,
        confirm_password: values.confirm_password,
      };

      const res = await axios.post(
        "https://billpay.mtmpay.in/Admin/api/admin/update-profile",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res?.data?.status === "1") {
        message.success(res?.data?.message || "Security credentials updated successfully.");
        setIsModalVisible(false);
        form.setFieldsValue({
          old_password: "",
          new_password: "",
          confirm_password: "",
        });
        await fetchProfile();
      } else {
        message.error(res?.data?.message || "Profile update failed.");
      }
    } catch (error) {
      console.error("Profile Update Error:", error);
      message.error(error?.response?.data?.message || "Failed to update profile.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenEditModal = () => {
    form.setFieldsValue({
      name: profile?.name || "",
      email: profile?.email || "",
      old_password: "",
      new_password: "",
      confirm_password: "",
    });
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    form.setFieldsValue({
      name: profile?.name || "",
      email: profile?.email || "",
      old_password: "",
      new_password: "",
      confirm_password: "",
    });
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  // ================================
  // LOGIN HISTORY COLUMNS
  // ================================
  const loginColumns = [
    {
      title: "SR",
      width: 60,
      align: "center",
      render: (_, __, index) => <span className="audit-sr">{index + 1}</span>,
    },
    {
      title: "IP Address",
      dataIndex: "ip",
      key: "ip",
      width: 160,
      render: (ip) => (
        <Space size="small">
          <span className="audit-ip">{ip || "N/A"}</span>
          {ip && (
            <Tooltip title="Copy IP">
              <CopyOutlined
                className="copy-audit-icon"
                onClick={() => copyToClipboard(ip, "IP Address")}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Browser & Environment",
      dataIndex: "browser",
      key: "browser",
      width: 180,
      render: (browser, record) => (
        <div className="audit-device-wrap">
          <LaptopOutlined style={{ color: "#0284c7" }} />
          <span>{browser || record.platform || "Web Browser"}</span>
        </div>
      ),
    },
    {
      title: "Device / User Agent",
      dataIndex: "device",
      key: "device",
      width: 220,
      render: (device) => (
        <span className="audit-device-text">{device || "N/A"}</span>
      ),
    },
    {
      title: "Location (Access Node)",
      dataIndex: "address",
      key: "address",
      width: 240,
      render: (address) => (
        <div className="audit-location-wrap">
          <GlobalOutlined style={{ color: "#10b981" }} />
          <span>{address || "Node Location Logged"}</span>
        </div>
      ),
    },
    {
      title: "Login Time (IST)",
      dataIndex: "login_time",
      key: "login_time",
      width: 190,
      render: (time) => <span className="audit-timestamp">{time || "N/A"}</span>,
    },
  ];

  if (isProfileLoading) {
    return (
      <div className="profile-loading-center">
        <Spin size="large" tip="Loading security console..." />
      </div>
    );
  }

  return (
    <div className="admin-profile-container">
      {/* 1. ENTERPRISE HEADER & PROFILE HERO */}
      <Card className="profile-header-card" bordered={false}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <div className="profile-hero-flex">
              <Avatar
                size={76}
                className="admin-avatar-chip"
                icon={<UserOutlined />}
              >
                {profile?.name ? profile.name.charAt(0).toUpperCase() : "A"}
              </Avatar>

              <div className="profile-hero-meta">
                <div className="name-badge-row">
                  <Title level={3} className="admin-title">
                    {profile?.name || "MTMPAY Central Administrator"}
                  </Title>
                  <Tag color="cyan" className="role-tag">
                    Super Admin
                  </Tag>
                  <Tag color="success" icon={<CheckCircleFilled />} className="status-tag">
                    Active Session
                  </Tag>
                </div>
                <div className="email-row">
                  <MailOutlined className="icon-sub" />
                  <span className="email-text">{profile?.email || "info@mtmpay.in"}</span>
                  <Divider type="vertical" />
                  <SafetyCertificateOutlined className="icon-sub" />
                  <span className="env-text">Central Switch Ops</span>
                </div>
              </div>
            </div>
          </Col>

          <Col xs={24} md={8} className="profile-hero-actions">
            <Space>
              <Button
                type="primary"
                icon={<KeyOutlined />}
                size="large"
                onClick={handleOpenEditModal}
                className="edit-credentials-btn"
              >
                Security Credentials
              </Button>
              <Button
                icon={<ReloadOutlined />}
                size="large"
                onClick={() => {
                  fetchProfile();
                  fetchLoginHistory();
                }}
                className="profile-refresh-btn"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 2. SECURITY & COMPLIANCE SUMMARY TILES */}
      <Row gutter={[16, 16]} className="mt-16">
        <Col xs={24} sm={8}>
          <Card className="profile-telemetry-tile" bordered={false}>
            <div className="tile-icon bg-cyan">
              <SafetyCertificateOutlined style={{ color: "#0284c7" }} />
            </div>
            <div className="tile-info">
              <span className="tile-label">Access Privileges</span>
              <span className="tile-value">Super Admin (Tier-1)</span>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card className="profile-telemetry-tile" bordered={false}>
            <div className="tile-icon bg-emerald">
              <ThunderboltFilled style={{ color: "#10b981" }} />
            </div>
            <div className="tile-info">
              <span className="tile-label">Encryption Protocol</span>
              <span className="tile-value">TLS v1.3 • AES-256</span>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card className="profile-telemetry-tile" bordered={false}>
            <div className="tile-icon bg-purple">
              <SafetyCertificateOutlined style={{ color: "#7c3aed" }} />
            </div>
            <div className="tile-info">
              <span className="tile-label">Audit Compliance</span>
              <span className="tile-value">RBI / DPDP Logged</span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 3. MASTER ACCOUNT SPECIFICATIONS */}
      <div className="mt-16">
        <Card
          title={
            <div className="section-card-title">
              <UserOutlined style={{ color: "#0284c7", marginRight: 8 }} />
              Master Administrative Account Specifications
            </div>
          }
          className="profile-detail-card"
          bordered={false}
        >
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="middle">
            <Descriptions.Item label="Authorized Entity">
              <Text strong>{profile?.name || "MTMPAY"}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Primary Access Contact">
              <Text copyable>{profile?.email || "info@mtmpay.in"}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Role Authorization">
              <Tag color="blue">Root Administrator</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Authentication Rail">
              <Tag color="cyan">Multi-Factor OTP Protected</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Platform Settlement Scope">
              <span>Bharat Connect, Cards, Vouchers &amp; Direct Services</span>
            </Descriptions.Item>
            <Descriptions.Item label="Default Timezone">
              <span>IST (UTC +05:30)</span>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </div>

      {/* 4. AUDIT JOURNAL (LOGIN HISTORY) */}
      <div className="mt-16">
        <Card
          title={
            <div className="section-card-title">
              <ClockCircleFilled style={{ color: "#7c3aed", marginRight: 8 }} />
              Administrator Security &amp; Session Audit Journal
            </div>
          }
          className="profile-detail-card"
          bordered={false}
        >
          <Table
            columns={loginColumns}
            dataSource={loginHistoryData}
            loading={loginHistoryLoading}
            rowKey={(record, index) => record._id || index}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total) => `Total ${total} authentication records`,
            }}
            scroll={{ x: 1000 }}
            className="enterprise-profile-table"
          />
        </Card>
      </div>

      {/* 5. SECURITY CREDENTIALS MODAL */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
            <KeyOutlined style={{ color: "#0284c7" }} />
            <span>Update Security &amp; Access Credentials</span>
          </div>
        }
        open={isModalVisible}
        onCancel={handleCloseModal}
        confirmLoading={isUpdating}
        okText="Update Credentials"
        cancelText="Cancel"
        destroyOnClose={false}
        width={560}
        className="profile-security-modal"
        onOk={() => {
          form
            .validateFields()
            .then((values) => {
              handleUpdate(values);
            })
            .catch((info) => {
              console.log("Validate Failed:", info);
            });
        }}
      >
        <Form layout="vertical" form={form}>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ color: "#0f172a", fontSize: 13, textTransform: "uppercase" }}>
              Administrative Identity
            </Text>
          </div>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label="Admin Name"
                name="name"
                rules={[{ required: true, message: "Please enter name" }]}
              >
                <Input placeholder="Enter administrator name" size="large" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Admin Email"
                name="email"
                rules={[
                  { required: true, message: "Please enter email" },
                  { type: "email", message: "Enter a valid email" },
                ]}
              >
                <Input placeholder="Enter admin email" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "14px 0" }} />

          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ color: "#0f172a", fontSize: 13, textTransform: "uppercase" }}>
              <LockOutlined style={{ marginRight: 6, color: "#059669" }} />
              Cryptographic Password Update
            </Text>
          </div>

          <Form.Item
            label="Current Password"
            name="old_password"
            rules={[{ required: true, message: "Current password is required to verify identity" }]}
          >
            <Input.Password placeholder="Enter current password" size="large" />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label="New Password"
                name="new_password"
                rules={[
                  { required: true, message: "Please enter new password" },
                  { min: 6, message: "Minimum 6 characters required" },
                ]}
              >
                <Input.Password placeholder="Enter new password" size="large" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Confirm New Password"
                name="confirm_password"
                dependencies={["new_password"]}
                rules={[
                  { required: true, message: "Please confirm password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("new_password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match!"));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm password" size="large" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;